import { useEffect, useState } from 'react';

const splitIds = (idsKey: string): string[] => (idsKey ? idsKey.split(',') : []);

/** No initial target → everything expanded; a target → only it (falling back to the first
 * section if the target id no longer exists among `ids`); no sections → nothing expanded. */
const computeExpandedIds = (ids: string[], initial?: string): Set<string> => {
  if (!ids.length) return new Set();
  if (!initial) return new Set(ids);
  return new Set([ids.includes(initial) ? initial : ids[0]]);
};

/**
 * Expansion state for operation-grouped sections (sidebars/lists).
 *
 * Deliberately keyed off the section ids joined into a string, not the sections array/object
 * itself: callers typically recompute their sections array (a new identity) on every unrelated
 * data change, e.g. after an upload or delete, even when the set of operation ids is unchanged.
 * Depending on the array reference would wipe a user's manual collapse/expand choices on every
 * such update.
 *
 * Lazy initializer so the very first paint already reflects the right default — an effect alone
 * would render everything collapsed for one frame, then flip open/closed after mount. The effect
 * stays to re-derive the defaults if the operation id set or the target changes later.
 */
export const useOperationSectionExpansion = (
  sectionIdsKey: string,
  initialExpandedOperationId?: string,
): { expandedIds: Set<string>; toggle: (id: string) => void } => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    computeExpandedIds(splitIds(sectionIdsKey), initialExpandedOperationId),
  );

  useEffect(() => {
    setExpandedIds(computeExpandedIds(splitIds(sectionIdsKey), initialExpandedOperationId));
  }, [sectionIdsKey, initialExpandedOperationId]);

  const toggle = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return { expandedIds, toggle };
};
